/**
 * @fileoverview Funciones principales de normalización de coordenadas PTEL
 * @module lib/coordinate/normalizer
 * 
 * Extraído de coordinateNormalizer.ts v2.12 como parte de TD-002.D
 * Contiene las funciones de normalización y procesamiento de pares de coordenadas.
 * 
 * @version 1.0.0
 * @date 2026-01-27
 */

// ============================================================================
// IMPORTS INTERNOS (módulo coordinate/)
// ============================================================================

import { RANGOS_ANDALUCIA } from './constants';
import { 
  esPlaceholder, 
  esCodigo, 
  detectarParEspaciosMultiples,
  detectarPatron 
} from './detectors';
import { 
  separarCoordenadasConcatenadas, 
  separarCoordenadasPegadas 
} from './separators';
import { parseDMS } from './parsers/dms';
import { parseNMEA, parseParNMEA } from './parsers/nmea';
import { detectarFormatoEspacial } from './parsers/spatial';
import { validarCoordenada, detectarIntercambioXY, intentarRescateSwap } from './validators';
import type { 
  ResultadoNormalizacion, 
  ResultadoValidacion,
  ParCoordenadas, 
  PatronDetectado,
  NivelConfianza 
} from './types';

// ============================================================================
// IMPORTS EXTERNOS (módulos superiores)
// ============================================================================

import { correctEncoding } from '../EncodingCorrector';
import { isSuspicious } from '../encodingDetector';
import { correctOCR, hasOCRErrors } from '../ocrCorrector';
import type { ResultadoDistanciaCentroide } from '../CoordinateValidator';
import { validarDistanciaCentroide } from '../CoordinateValidator';
import { 
  detectTruncation, 
  isPotentiallyTruncated,
  type TruncationResult 
} from '../detectors/TruncatedCoordinateDetector';
import { generateDigitRemovalCandidates } from './coordinateVariants';
import { rankCoordinateVariants } from '../coordinateVariantRanking';
import proj4 from 'proj4';

// Registrar definiciones EPSG necesarias para NRM-A01 y NRM-B01
// (coordinateUtils.ts las registra también, pero normalizer.ts puede cargarse antes)
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs +type=crs');
}
if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs');
}
if (!proj4.defs('EPSG:23030')) {
  // towgs84=-87,-98,-121 son los parámetros medios IGN para la Península (EPSG:1632)
  // Necesarios para que proj4 encadene ED50→WGS84→ETRS89 correctamente (~2m precisión)
  proj4.defs('EPSG:23030', '+proj=utm +zone=30 +ellps=intl +towgs84=-87,-98,-121,0,0,0,0 +units=m +no_defs +type=crs');
}

// ============================================================================
// NRM-B01: FUNCIÓN PURA DE TRANSFORMACIÓN ED50 → ETRS89
// ============================================================================

/**
 * Transforma coordenadas UTM ED50 zona 30N (EPSG:23030)
 * a ETRS89 UTM zona 30N (EPSG:25830) mediante Helmert 7p (EPSG:1632).
 *
 * Precisión resultante: ~1,5–3 m (suficiente para validación PTEL).
 * Para cartografía de precisión usar la rejilla NTv2 del IGN.
 *
 * @param xEd50  Coordenada X en ED50/UTM-30N (metros)
 * @param yEd50  Coordenada Y en ED50/UTM-30N (metros)
 * @returns      { x, y } en ETRS89/UTM-30N (EPSG:25830)
 */
export function transformarED50aETRS89(
  xEd50: number,
  yEd50: number
): { x: number; y: number } {
  const [xEtrs, yEtrs] = proj4('EPSG:23030', 'EPSG:25830', [xEd50, yEd50]);
  return { x: xEtrs, y: yEtrs };
}

// ============================================================================
// CONSTANTES INTERNAS
// ============================================================================

/**
 * Umbral de distancia para generar WARNING (km)
 * Coordenadas a más de 15km del centroide municipal son sospechosas
 */
const UMBRAL_DISTANCIA_WARNING_KM = 15;

/**
 * Umbral de distancia para degradar confianza a BAJA (km)
 * Coordenadas a más de 30km del centroide municipal son muy sospechosas
 */
const UMBRAL_DISTANCIA_BAJA_KM = 30;

/**
 * Umbral para detectar Y potencialmente truncada (< 1M sugiere dígitos faltantes)
 */
const _UMBRAL_Y_TRUNCADA = 1_000_000;

/**
 * Rango válido de coordenadas Y UTM para Andalucía
 */
const Y_MIN_ANDALUCIA = 3_990_000;  // Sur (Tarifa)
const Y_MAX_ANDALUCIA = 4_290_000;  // Norte (Sierra Morena)

/**
 * Distancia máxima aceptable para aplicar corrección automática (km)
 */
const UMBRAL_CORRECCION_DISTANCIA_KM = 30;

/**
 * Score mínimo de variante para aplicar corrección automática de dígito extra (F2.06)
 * Un score ≥50 indica que la candidata cae dentro del municipio a distancia razonable
 */
const UMBRAL_SCORE_VARIANTE = 50;

// ============================================================================
// FUNCIÓN PRINCIPAL: NORMALIZAR COORDENADA INDIVIDUAL
// ============================================================================

/**
 * Normaliza una coordenada individual desde múltiples formatos a número.
 * 
 * Pipeline de normalización en 6 fases:
 * - FASE 0: Detección (placeholder, código, concatenados, espaciales, DMS, NMEA, OCR)
 * - FASE 1: Corrección Mojibake (UTF-8)
 * - FASE 2: Caracteres especiales (comillas, espacios especiales, doble punto)
 * - FASE 3: Espacios y decimales implícitos
 * - FASE 4: Formato europeo (puntos miles, coma decimal)
 * - FASE 5: Parsing final a número
 * 
 * @param input - Valor de coordenada en cualquier formato
 * @returns Resultado con valor normalizado, patrón detectado y trazabilidad
 * 
 * @example
 * ```typescript
 * // Formato europeo
 * normalizarCoordenada("453.821,45")
 * // { valorNormalizado: 453821.45, patronDetectado: 'EUROPEO_COMPLETO', exito: true }
 * 
 * // DMS
 * normalizarCoordenada("37°26'46.5\"N")
 * // { valorNormalizado: 37.4462..., patronDetectado: 'DMS_ESTANDAR', exito: true }
 * ```
 */
export function normalizarCoordenada(input: string): ResultadoNormalizacion {
  const resultado: ResultadoNormalizacion = {
    valorOriginal: input,
    valorNormalizado: null,
    exito: false,
    patronDetectado: 'DESCONOCIDO',
    fasesAplicadas: [],
    warnings: [],
    errores: [],
  };
  
  // Coerción defensiva a string (pueden llegar números, null, undefined)
  let valor = input != null ? String(input) : '';
  
  valor = valor.trim();
  resultado.fasesAplicadas.push('FASE_0_TRIM');
  
  // FASE 0.0: Placeholder
  if (esPlaceholder(valor)) {
    resultado.patronDetectado = 'PLACEHOLDER';
    resultado.fasesAplicadas.push('FASE_0_PLACEHOLDER');
    resultado.warnings.push('Valor placeholder detectado');
    return resultado;
  }
  
  // FASE 0.1: Pre-filtro de códigos (no coordenadas)
  const prefiltro = esCodigo(valor);
  if (prefiltro.esCodigo) {
    resultado.patronDetectado = 'CODIGO_NO_COORDENADA';
    resultado.fasesAplicadas.push('FASE_0.1_PREFILTRO_CODIGO');
    resultado.errores.push(prefiltro.razon || 'Valor detectado como código, no coordenada');
    return resultado;
  }
  
  // FASE 0.2: Detectar pares X/Y concatenados con espacios múltiples
  const parEspacios = detectarParEspaciosMultiples(valor);
  if (parEspacios.esPar) {
    if (parEspacios.valido && parEspacios.valorX !== null) {
      resultado.patronDetectado = 'PAR_ESPACIOS_MULTIPLES';
      resultado.valorNormalizado = parEspacios.valorX;
      resultado.exito = true;
      resultado.fasesAplicadas.push('FASE_0.2_PAR_ESPACIOS');
      resultado.warnings.push(parEspacios.warning || 'Par X/Y detectado en una celda');
      (resultado)._yExtraida = parEspacios.valorY ?? undefined;
      return resultado;
    } else {
      resultado.patronDetectado = 'PAR_ESPACIOS_MULTIPLES';
      resultado.fasesAplicadas.push('FASE_0.2_PAR_ESPACIOS');
      resultado.errores.push(parEspacios.warning || 'Par X/Y detectado pero valores fuera de rango');
      return resultado;
    }
  }
  
  resultado.patronDetectado = detectarPatron(valor);
  
  // FASE 0.5: Coordenadas concatenadas
  if (resultado.patronDetectado === 'CONCATENADO') {
    const separacion = separarCoordenadasConcatenadas(valor);
    if (separacion.esConcatenado && separacion.valorX !== null) {
      resultado.valorNormalizado = separacion.valorX;
      resultado.exito = true;
      resultado.fasesAplicadas.push('FASE_0.5_CONCATENADO');
      resultado.warnings.push(separacion.warning || 'Coordenadas concatenadas detectadas');
      (resultado)._yExtraida = separacion.valorY ?? undefined;
      (resultado)._confianzaConcatenacion = separacion.confianza ?? undefined;
      return resultado;
    }
  }
  
  // FASE 0.55: BE-01 Coordenadas pegadas (13-14 dígitos Y+X)
  if (resultado.patronDetectado === 'PEGADAS') {
    const pegadas = separarCoordenadasPegadas(valor);
    if (pegadas.esPegadas && pegadas.valorX !== null && pegadas.valorY !== null) {
      resultado.valorNormalizado = pegadas.valorX;
      resultado.exito = true;
      resultado.fasesAplicadas.push('FASE_0.55_PEGADAS');
      resultado.warnings.push(pegadas.warning || 'Coordenadas pegadas separadas');
      (resultado)._yExtraida = pegadas.valorY ?? undefined;
      (resultado)._confianzaPegadas = pegadas.confianza ?? undefined;
      return resultado;
    }
  }
  
  // FASE 0.6: Formatos espaciales (WKT, GeoJSON)
  if (resultado.patronDetectado === 'WKT_POINT' || resultado.patronDetectado === 'GEOJSON_POINT') {
    const espacial = detectarFormatoEspacial(valor);
    if (espacial.esEspacial && espacial.valorX !== null) {
      resultado.valorNormalizado = espacial.valorX;
      resultado.exito = true;
      resultado.fasesAplicadas.push('FASE_0.6_FORMATO_ESPACIAL');
      resultado.warnings.push(espacial.warning || 'Formato espacial detectado');
      (resultado)._yExtraida = espacial.valorY ?? undefined;
      (resultado)._formatoEspacial = espacial.formato ?? undefined;
      return resultado;
    }
  }
  
  // FASE 0.7: Formatos DMS (Grados Sexagesimales)
  const patronesDMS: PatronDetectado[] = [
    'DMS_ESTANDAR', 'DMS_ESPACIOS', 'DMS_PREFIJO', 
    'DMS_MINUTOS_DEC', 'DMS_ESPACIOS_EXTRA', 'DMS_SIGNO', 'DMS_DOS_PUNTOS'
  ];
  
  if (patronesDMS.includes(resultado.patronDetectado)) {
    const dms = parseDMS(valor);
    if (dms.esValido && dms.valorDecimal !== null) {
      resultado.valorNormalizado = dms.valorDecimal;
      resultado.exito = true;
      resultado.fasesAplicadas.push('FASE_0.7_DMS');
      if (dms.warning) {
        resultado.warnings.push(dms.warning);
      }
      resultado.warnings.push(
        `DMS convertido: ${dms.grados}°${dms.minutos}'${dms.segundos}"${dms.direccion || ''} → ${dms.valorDecimal.toFixed(6)}°`
      );
      (resultado)._dmsOriginal = {
        grados: dms.grados!,
        minutos: dms.minutos!,
        segundos: dms.segundos!,
        direccion: dms.direccion ?? undefined
      };
      (resultado)._esLatitud = dms.esLatitud ?? undefined;
      (resultado)._esGeografica = true;
      return resultado;
    }
  }
  
  // FASE 0.8: Formatos NMEA GPS (D1-D4)
  const patronesNMEA: PatronDetectado[] = [
    'NMEA_ESTANDAR', 'NMEA_ENTERO', 'NMEA_SENTENCIA', 'NMEA_PAR'
  ];
  
  if (patronesNMEA.includes(resultado.patronDetectado)) {
    const nmea = parseNMEA(valor);
    if (nmea.esValido && nmea.valorDecimal !== null) {
      resultado.valorNormalizado = nmea.valorDecimal;
      resultado.exito = true;
      resultado.fasesAplicadas.push('FASE_0.8_NMEA');
      if (nmea.warning) {
        resultado.warnings.push(nmea.warning);
      }
      resultado.warnings.push(
        `NMEA convertido: ${valor} → ${nmea.valorDecimal.toFixed(6)}° (${nmea.hemisferio || 'sin hemisferio'})`
      );
      (resultado)._esLatitud = nmea.esLatitud ?? undefined;
      (resultado)._esGeografica = true;
      (resultado)._hemisferio = nmea.hemisferio ?? undefined;
      return resultado;
    }
    
    const parNmea = parseParNMEA(valor);
    if (parNmea.exito && parNmea.latitud !== null) {
      resultado.valorNormalizado = parNmea.latitud;
      resultado.exito = true;
      resultado.fasesAplicadas.push('FASE_0.8_NMEA_PAR');
      resultado.warnings.push(
        `NMEA par detectado: lat=${parNmea.latitud.toFixed(6)}°, lon=${parNmea.longitud?.toFixed(6)}°`
      );
      (resultado)._yExtraida = parNmea.longitud ?? undefined;
      (resultado)._esLatitud = true;
      (resultado)._esGeografica = true;
      (resultado)._tipoSentencia = parNmea.tipoSentencia ?? undefined;
      return resultado;
    }
  }
  
  // FASE 0.9: Corrección errores OCR (QA-01)
  if (hasOCRErrors(valor)) {
    const valorAntesOCR = valor;
    const ocrResult = correctOCR(valor);
    
    if (ocrResult.wasModified) {
      valor = ocrResult.corrected;
      resultado.fasesAplicadas.push('FASE_0.9_OCR');
      resultado.warnings.push(`OCR corregido: "${valorAntesOCR}" → "${valor}"`);
    }
  }

  // FASE 1: Corrección Mojibake (UTF-8 → Latin1 → UTF-8)
  if (isSuspicious(valor)) {
    const valorAntes = valor;
    const correctionResult = correctEncoding(valor);
    
    if (correctionResult.wasModified) {
      valor = correctionResult.corrected;
      resultado.fasesAplicadas.push('FASE_1_MOJIBAKE');
      resultado.warnings.push(`Mojibake corregido: "${valorAntes}" → "${valor}"`);
      
      if (correctionResult.tiersApplied.length > 0) {
        resultado.warnings.push(`Tiers UTF-8: ${correctionResult.tiersApplied.join(', ')}`);
      }
    }
  }
  
  // FASE 2: Normalización caracteres especiales
  const valorAntesFase2 = valor;
  valor = valor.replace(/´´/g, '.');
  valor = valor.replace(/´/g, '.');
  valor = valor.replace(/['']/g, '.');
  valor = valor.replace(/'/g, '.');
  valor = valor.replace(/[""]/g, '.');
  
  // FASE 2.5: Normalizar espacios especiales a espacio ASCII estándar
  valor = valor.replace(/[\u00A0\u2002\u2003\u2009\u200A\u202F\u205F\u3000]/g, ' ');
  
  // FASE 2.6: FIX-DOUBLE_DOT - Doble punto como separador decimal
  const valorAntesDoblePunto = valor;
  valor = valor.replace(/(\d)\.\.(\d)/g, '$1.$2');
  if (valor !== valorAntesDoblePunto) {
    resultado.fasesAplicadas.push('FASE_2.6_DOUBLE_DOT');
    resultado.warnings.push(`Doble punto corregido: "${valorAntesDoblePunto}" → "${valor}"`);
  }
  
  // FASE 2.7: BE-01 COMA_ESPACIO_DECIMAL - Colomera format
  const valorAntesComaEspacio = valor;
  valor = valor.replace(/(\d),\s+(\d)/g, '$1.$2');
  if (valor !== valorAntesComaEspacio) {
    resultado.fasesAplicadas.push('FASE_2.7_COMA_ESPACIO');
    resultado.warnings.push(`Coma-espacio decimal corregido: "${valorAntesComaEspacio}" → "${valor}"`);
  }
  
  if (valor !== valorAntesFase2) {
    resultado.fasesAplicadas.push('FASE_2_CARACTERES_ESPECIALES');
  }
  
  // FASE 3: Eliminación de espacios
  const valorAntesFase3 = valor;
  const matchDecimalImplicito = valor.match(/^(\d{1,3}(?:\s+\d{3})*)\s+(\d{1,2})$/);
  if (matchDecimalImplicito && !valor.includes('.')) {
    const parteEntera = matchDecimalImplicito[1].replace(/\s+/g, '');
    const parteDecimal = matchDecimalImplicito[2];
    valor = `${parteEntera}.${parteDecimal}`;
    resultado.fasesAplicadas.push('FASE_3_DECIMAL_IMPLICITO');
    resultado.warnings.push(`Decimales implícitos detectados: "${valorAntesFase3}" → "${valor}"`);
  } else {
    valor = valor.replace(/\s+/g, '');
    if (valor !== valorAntesFase3.replace(/\s+/g, '')) {
      resultado.fasesAplicadas.push('FASE_3_ESPACIOS');
    }
  }
  
  // FASE 4: Normalización formato europeo
  if (/^\d{1,3}(?:\.\d{3})+,\d+$/.test(valor)) {
    valor = valor.replace(/\./g, '').replace(',', '.');
    resultado.fasesAplicadas.push('FASE_4_EUROPEO_COMPLETO');
  } else if (/^\d+,\d+$/.test(valor)) {
    valor = valor.replace(',', '.');
    resultado.fasesAplicadas.push('FASE_4_COMA_DECIMAL');
  } else if (/^\d{1,3}(?:\.\d{3})+$/.test(valor)) {
    valor = valor.replace(/\./g, '');
    resultado.fasesAplicadas.push('FASE_4_PUNTO_MILES');
  }
  
  // FASE 5: Limpieza final y parsing
  valor = valor.replace(/\.+/g, '.');
  valor = valor.replace(/\.$/, '');
  valor = valor.replace(/^\./, '');
  
  const numero = parseFloat(valor);
  
  if (isNaN(numero)) {
    resultado.errores.push(`No se pudo convertir a número: "${valor}"`);
    return resultado;
  }
  
  resultado.valorNormalizado = numero;
  resultado.exito = true;
  resultado.fasesAplicadas.push('FASE_5_PARSING');
  
  return resultado;
}

// ============================================================================
// FUNCIÓN PRINCIPAL: PROCESAR PAR DE COORDENADAS
// ============================================================================

/**
 * Opciones para procesarParCoordenadas
 */
export interface OpcionesProcesarPar {
  /** Aplicar corrección automática de errores P0 (default: true) */
  aplicarCorreccionP0?: boolean;
  /** Detectar y corregir intercambio X↔Y (default: true) */
  detectarIntercambio?: boolean;
  /** Detectar coordenadas concatenadas (default: true) */
  detectarConcatenacion?: boolean;
  /** EPSG asumido para las coordenadas (default: 25830) */
  epsgAsumido?: number;
  /** Código INE del municipio para validación geográfica (VAL-01) */
  codigoINE?: string;
  /**
   * NRM-B01: indicar explícitamente que las coordenadas están en ED50.
   * Cuando es 'ED50', el pipeline aplica la transformación Helmert 7p
   * sin necesidad de detectar la ventana de distancia heurística.
   */
  datumHint?: 'ED50' | 'ETRS89' | 'WGS84';
}

/**
 * Procesa un par de coordenadas X, Y aplicando el pipeline completo.
 * 
 * Funcionalidad:
 * - Normaliza ambas coordenadas individualmente
 * - Detecta y separa coordenadas concatenadas/pegadas
 * - Detecta formatos espaciales (WKT, GeoJSON)
 * - Valida rangos para Andalucía
 * - Corrige Y truncada (sin el "4" inicial)
 * - Detecta intercambio X↔Y
 * - Valida distancia al centroide municipal (si se proporciona codigoINE)
 * - Calcula nivel de confianza global
 * 
 * @param xInput - Valor de la coordenada X
 * @param yInput - Valor de la coordenada Y
 * @param opciones - Opciones de procesamiento
 * @returns Par de coordenadas procesado con metadata completa
 * 
 * @example
 * ```typescript
 * const par = procesarParCoordenadas("453821", "4085421");
 * // { x: 453821, y: 4085421, confianzaGlobal: 'ALTA', ... }
 * 
 * // Con validación geográfica
 * const parConINE = procesarParCoordenadas("453821", "85421", { codigoINE: "18087" });
 * // Y truncada corregida: 85421 → 4085421
 * ```
 */
export function procesarParCoordenadas(
  xInput: string,
  yInput: string,
  opciones: OpcionesProcesarPar = {}
): ParCoordenadas {
  const {
    aplicarCorreccionP0 = true,
    detectarIntercambio = true,
    detectarConcatenacion = true,
    epsgAsumido = 25830,
    codigoINE,
    datumHint,
  } = opciones;

  // NRM-B01: RAMA EXPLÍCITA — datumHint === 'ED50'
  // Cuando el caller conoce con certeza que las coordenadas están en ED50,
  // se aplica la transformación Helmert 7p directamente sin heurística de ventana.
  if (datumHint === 'ED50') {
    const _xNum = parseFloat(String(xInput).replace(',', '.'));
    const _yNum = parseFloat(String(yInput).replace(',', '.'));
    if (isFinite(_xNum) && isFinite(_yNum)) {
      const { x: xEtrs, y: yEtrs } = transformarED50aETRS89(_xNum, _yNum);
      const normXed50: ResultadoNormalizacion = {
        valorOriginal: String(xInput),
        valorNormalizado: xEtrs,
        exito: true,
        patronDetectado: 'LIMPIO' as const,
        fasesAplicadas: ['NRM-B01-HINT'],
        correccionAplicada: true,
        tipoCorreccion: 'ED50_TO_ETRS89' as const,
        warnings: [
          `[NRM-B01: transformado de ED50→ETRS89 Helmert ~2m] X: ${_xNum} → ${xEtrs.toFixed(1)}`,
        ],
        errores: [],
      };
      const normYed50: ResultadoNormalizacion = {
        valorOriginal: String(yInput),
        valorNormalizado: yEtrs,
        exito: true,
        patronDetectado: 'LIMPIO' as const,
        fasesAplicadas: ['NRM-B01-HINT'],
        correccionAplicada: false,
        tipoCorreccion: null,
        warnings: [`[NRM-B01: transformado de ED50→ETRS89 Helmert ~2m] Y: ${_yNum} → ${yEtrs.toFixed(1)}`],
        errores: [],
      };
      const validGeo = codigoINE ? validarDistanciaCentroide(xEtrs, yEtrs, codigoINE) : null;
      return {
        x: xEtrs,
        y: yEtrs,
        xOriginal: String(xInput),
        yOriginal: String(yInput),
        normalizacionX: normXed50,
        normalizacionY: normYed50,
        validacionX: null,
        validacionY: null,
        intercambioAplicado: false,
        concatenacionDetectada: false,
        confianzaGlobal: 'MEDIA' as const, // MEDIA: conversión implícita, no verificada in-situ
        epsg: epsgAsumido,
        validacionGeografica: validGeo,
        codigoINE,
        datumOriginal: 'ED50' as const,
      };
    }
  }
  
  // Coerción defensiva a string
  const xStr = xInput != null ? String(xInput) : '';
  const yStr = yInput != null ? String(yInput) : '';

  // NRM-A01-C4: COHERENCIA CON CENTROIDE MUNICIPAL (MEJ-01 Capa 4)
  // La capa más potente del detector de datum. Actúa como árbitro final ANTES de que
  // las capas C1-C3 y NRM-A01 tomen decisiones, siempre que codigoINE esté disponible.
  //
  // Estrategia:
  //   1. Probar x/y como UTM30 directos → si dist < UMBRAL → confirmar UTM30 (sin modificar)
  //   2. Probar x/y como WGS84 lon/lat → convertir y comparar con centroide
  //   3. Probar x*1000 / y*1000 como UTM30 con miles implícitos → si dist < UMBRAL → corregir
  //
  // Umbrales: 50 km (coherente con el municipio más extenso de Andalucía, Iznájar ~140 km²,
  // radio equivalente ~6.7 km; usamos 50 km para abarcar municipios con pedanías dispersas
  // como Guadix o Castril, sin incluir municipios ajenos).
  //
  // Guarda: solo actúa si codigoINE está disponible y los valores son numéricos finitos.
  // No bloquea el flujo normal: si ninguna hipótesis da < 50 km, continúa sin modificar.
  if (codigoINE) {
    const _C4_UMBRAL_KM = 50;

    const _c4XNum = parseFloat(xStr.replace(',', '.'));
    const _c4YNum = parseFloat(yStr.replace(',', '.'));

    if (isFinite(_c4XNum) && isFinite(_c4YNum)) {
      // Hipótesis 1: x/y ya son UTM30 directos (el caso más común)
      const _distUtm = validarDistanciaCentroide(_c4XNum, _c4YNum, codigoINE);
      if (_distUtm.exito && _distUtm.distanciaKm < _C4_UMBRAL_KM) {
        // Coordenadas confirmadas como UTM30 — dejar pasar al flujo normal
        // No retornamos early; dejamos que el pipeline completo (validaciones, swap, etc.) siga.
        // Marcamos el resultado con UTM30_CENTROID_CONFIRMED al final vía normX metadata.
        // (La confirmación real ocurre en la validacionGeografica más abajo)
        // → no early return: el flujo normal ya maneja este caso correctamente.
      } else {
        // Hipótesis 2: interpretar como WGS84 decimal (lon, lat)
        // Solo si valores están en rango geográfico aproximado para España/Andalucía ampliado
        // (±15° alrededor del centro de Andalucía para tolerar errores de transcripción)
        const _c4EsLonRango = _c4XNum > -0.9 && _c4XNum <= 10;
        const _c4EsLatRango = _c4YNum >= 28 && _c4YNum <= 50;
        if (_c4EsLonRango && _c4EsLatRango) {
          try {
            const [_c4UtmX, _c4UtmY] = proj4('EPSG:4326', 'EPSG:25830', [_c4XNum, _c4YNum]);
            const _distWgs = validarDistanciaCentroide(_c4UtmX, _c4UtmY, codigoINE);
            if (_distWgs.exito && _distWgs.distanciaKm < _C4_UMBRAL_KM) {
              return {
                x: _c4UtmX, y: _c4UtmY,
                xOriginal: xStr, yOriginal: yStr,
                normalizacionX: {
                  valorOriginal: xStr,
                  valorNormalizado: _c4UtmX, exito: true, patronDetectado: 'LIMPIO' as const,
                  fasesAplicadas: ['NRM-A01-C4'],
                  correccionAplicada: true, tipoCorreccion: 'WGS84_CENTROID_CONVERTED' as const,
                  warnings: [
                    `C4-centroide: WGS84 (lon=${_c4XNum}, lat=${_c4YNum}) → UTM30 ` +
                    `(${_c4UtmX.toFixed(0)}, ${_c4UtmY.toFixed(0)}) dist=${_distWgs.distanciaKm.toFixed(1)}km`,
                  ],
                  errores: [],
                } as ResultadoNormalizacion,
                normalizacionY: {
                  valorOriginal: yStr,
                  valorNormalizado: _c4UtmY, exito: true, patronDetectado: 'LIMPIO' as const,
                  fasesAplicadas: ['NRM-A01-C4'], correccionAplicada: false, tipoCorreccion: null,
                  warnings: [], errores: [],
                } as ResultadoNormalizacion,
                validacionX: null, validacionY: null,
                intercambioAplicado: false, concatenacionDetectada: false,
                confianzaGlobal: 'MEDIA' as const, // MEDIA: conversión implicita desde WGS84
                epsg: epsgAsumido,
                validacionGeografica: _distWgs,
                codigoINE,
              };
            }
          } catch {
            // proj4 puede fallar con valores extremos; ignorar y continuar
          }
        }

        // Hipótesis 3: miles implícitos (x*1000 / y*1000 como UTM30)
        // Solo si x e y son razonablemente pequeños (tipo "115" / "4130" sin decimales)
        const _c4XMiles = _c4XNum * 1000;
        const _c4YMiles = _c4YNum * 1000;
        const _c4XMilesEnRango = _c4XMiles >= 100_000 && _c4XMiles <= 700_000;
        const _c4YMilesEnRango = _c4YMiles >= 3_900_000 && _c4YMiles <= 4_300_000;
        if (_c4XMilesEnRango && _c4YMilesEnRango) {
          const _distMiles = validarDistanciaCentroide(_c4XMiles, _c4YMiles, codigoINE);
          if (_distMiles.exito && _distMiles.distanciaKm < _C4_UMBRAL_KM) {
            return {
              x: _c4XMiles, y: _c4YMiles,
              xOriginal: xStr, yOriginal: yStr,
              normalizacionX: {
                valorOriginal: xStr,
                valorNormalizado: _c4XMiles, exito: true, patronDetectado: 'PUNTO_MILES' as const,
                fasesAplicadas: ['NRM-A01-C4'],
                correccionAplicada: true, tipoCorreccion: 'UTM30_MILES_EUROPEOS' as const,
                warnings: [
                  `C4-centroide: miles implícitos ${_c4XNum}→${_c4XMiles} / ` +
                  `${_c4YNum}→${_c4YMiles} dist=${_distMiles.distanciaKm.toFixed(1)}km`,
                ],
                errores: [],
              } as ResultadoNormalizacion,
              normalizacionY: {
                valorOriginal: yStr,
                valorNormalizado: _c4YMiles, exito: true, patronDetectado: 'PUNTO_MILES' as const,
                fasesAplicadas: ['NRM-A01-C4'],
                correccionAplicada: true, tipoCorreccion: 'UTM30_MILES_EUROPEOS' as const,
                warnings: [`C4-centroide: miles implícitos ${_c4YNum}→${_c4YMiles}`],
                errores: [],
              } as ResultadoNormalizacion,
              validacionX: null, validacionY: null,
              intercambioAplicado: false, concatenacionDetectada: false,
              confianzaGlobal: 'ALTA' as const,
              epsg: epsgAsumido,
              validacionGeografica: _distMiles,
              codigoINE,
            };
          }
        }
      }
    }
  }

  // NRM-A01: PRE-DETECCIÓN WGS84 DECIMAL
  // Rango Andalucía: lon ∈ [-9.5, -0.9], lat ∈ [35.9, 38.8]
  // Sin solapamiento posible con UTM30 → falso positivo = 0
  {
    const _xNum = parseFloat(xStr.replace(',', '.'));
    const _yNum = parseFloat(yStr.replace(',', '.'));
    const _esLon = isFinite(_xNum) && _xNum >= -9.5 && _xNum <= -0.9;
    const _esLat = isFinite(_yNum) && _yNum >= 35.9 && _yNum <= 38.8;
    if (_esLon && _esLat) {
      const [utmX, utmY] = proj4('EPSG:4326', 'EPSG:25830', [_xNum, _yNum]);
      return {
        x: utmX, y: utmY,
        xOriginal: xStr, yOriginal: yStr,
        normalizacionX: {
          valorOriginal: xStr,
          valorNormalizado: utmX, exito: true, patronDetectado: 'LIMPIO' as const,
          fasesAplicadas: ['NRM-A01'],
          correccionAplicada: true, tipoCorreccion: 'WGS84_TO_UTM30' as const,
          warnings: [`WGS84 decimal detectado (lon=${_xNum}, lat=${_yNum}) → convertido a UTM30 ETRS89`],
          errores: [],
        } as ResultadoNormalizacion,
        normalizacionY: {
          valorOriginal: yStr,
          valorNormalizado: utmY, exito: true, patronDetectado: 'LIMPIO' as const,
          fasesAplicadas: ['NRM-A01'],
          correccionAplicada: false, tipoCorreccion: null, warnings: [], errores: [],
        } as ResultadoNormalizacion,
        validacionX: null, validacionY: null,
        intercambioAplicado: false, concatenacionDetectada: false,
        confianzaGlobal: 'MEDIA' as const,
        epsg: epsgAsumido,
        validacionGeografica: null,
        codigoINE,
      };
    }
  }

  // NRM-A01-C5: DETECTOR WGS84 CON EJES INVERTIDOS (lat, lon) → swap + conversión
  // Caso real documentado: municipios andaluces que insertan (lat, lon) en columnas (X, Y).
  // Zalabí (antigua y nueva): xOriginal="37.26", yOriginal="-3.09"
  //
  // Condiciones de activación — sin solapamiento posible con UTM30:
  //   xStr parse → _lat ∈ [35.9, 38.8]   (rango lat Andalucía; UTM30 X ∈ [100k, 800k])
  //   yStr parse → _lon ∈ [-9.5, -0.9]   (rango lon Andalucía negativo; UTM30 Y ∈ [3.9M, 4.4M])
  // Acción: swap → (lon, lat) → proj4 EPSG:4326 → EPSG:25830
  {
    const _latCand = parseFloat(xStr.replace(',', '.'));
    const _lonCand = parseFloat(yStr.replace(',', '.'));
    const _esLatEnX = isFinite(_latCand) && _latCand >= 35.9 && _latCand <= 38.8;
    const _esLonEnY = isFinite(_lonCand) && _lonCand >= -9.5 && _lonCand <= -0.9;
    if (_esLatEnX && _esLonEnY) {
      const [utmX, utmY] = proj4('EPSG:4326', 'EPSG:25830', [_lonCand, _latCand]);
      return {
        x: utmX, y: utmY,
        xOriginal: xStr, yOriginal: yStr,
        normalizacionX: {
          valorOriginal: xStr,
          valorNormalizado: utmX, exito: true, patronDetectado: 'LIMPIO' as const,
          fasesAplicadas: ['NRM-A01-C5'],
          correccionAplicada: true, tipoCorreccion: 'WGS84_INV_TO_UTM30' as const,
          warnings: [`WGS84 ejes invertidos (lat=${_latCand}, lon=${_lonCand}) → swap + UTM30 ETRS89`],
          errores: [],
        } as ResultadoNormalizacion,
        normalizacionY: {
          valorOriginal: yStr,
          valorNormalizado: utmY, exito: true, patronDetectado: 'LIMPIO' as const,
          fasesAplicadas: ['NRM-A01-C5'],
          correccionAplicada: true, tipoCorreccion: 'WGS84_INV_TO_UTM30' as const,
          warnings: [], errores: [],
        } as ResultadoNormalizacion,
        validacionX: null, validacionY: null,
        intercambioAplicado: true, concatenacionDetectada: false,
        confianzaGlobal: 'MEDIA' as const,
        epsg: epsgAsumido,
        validacionGeografica: null,
        codigoINE,
      };
    }
  }

  // NRM-A01-C2-C3: DETECTOR DATUM — CAPAS 2 Y 3 (MEJ-01)
  // Resuelve el caso donde separadores de miles europeos ("115.058"/"4.130.709") llegan
  // sin limpiar y NRM-A01 (C1) no los detecta porque no son lon/lat negativos.
  //
  // Capa 2: número de dígitos enteros
  //   UTM30 X Andalucía → 5-6 dígitos (100.000–699.999)
  //   UTM30 Y Andalucía → 7 dígitos   (3.900.000–4.299.999)
  // Capa 3: ratio Y/X
  //   UTM30 Andalucía: Y/X ∈ [5.5, 40]  (e.g. Ayamonte: 4.130.709/115.058 ≈ 35.9)
  //   Activar solo cuando C2 también lo confirma → falso positivo ≈ 0
  {
    const MILES_EU_RE = /^\d{1,3}(?:\.\d{3})+$/;

    const _xMilesEu = MILES_EU_RE.test(xStr.trim());
    const _yMilesEu = MILES_EU_RE.test(yStr.trim());

    // Limpiar separadores de miles para evaluar el valor real
    const _xLimpio = _xMilesEu
      ? parseFloat(xStr.replace(/\./g, ''))
      : parseFloat(xStr.replace(',', '.'));
    const _yLimpio = _yMilesEu
      ? parseFloat(yStr.replace(/\./g, ''))
      : parseFloat(yStr.replace(',', '.'));

    if (isFinite(_xLimpio) && isFinite(_yLimpio) && _xLimpio > 0 && _yLimpio > 0) {
      // Capa 2: longitud de parte entera
      const _xDigits = String(Math.floor(_xLimpio)).length;
      const _yDigits = String(Math.floor(_yLimpio)).length;
      const _c2Utm = (_xDigits >= 5 && _xDigits <= 6) && _yDigits === 7;

      // Capa 3: ratio Y/X ∈ [4, 50] con X > 0
      const _ratio = _yLimpio / _xLimpio;
      const _c3Utm = _ratio >= 4 && _ratio <= 50;

      // Solo actuar si C2 + C3 coinciden Y al menos un campo tenía miles europeos
      // (sin ese requisito podríamos interferir con UTM limpio ya gestionado por FASE 4)
      if (_c2Utm && _c3Utm && (_xMilesEu || _yMilesEu)) {
        return {
          x: _xLimpio, y: _yLimpio,
          xOriginal: xStr, yOriginal: yStr,
          normalizacionX: {
            valorOriginal: xStr,
            valorNormalizado: _xLimpio,
            exito: true,
            patronDetectado: 'EUROPEO_COMPLETO' as const,
            fasesAplicadas: ['NRM-A01-C2', 'NRM-A01-C3'],
            correccionAplicada: _xMilesEu,
            tipoCorreccion: _xMilesEu ? ('UTM30_MILES_EUROPEOS' as const) : null,
            warnings: [
              `UTM30 por capas 2+3: dígitos(X)=${_xDigits} dígitos(Y)=${_yDigits} ratio=${_ratio.toFixed(1)}` +
              (_xMilesEu ? ` | miles-EU: "${xStr}" → ${_xLimpio}` : ''),
            ],
            errores: [],
          } as ResultadoNormalizacion,
          normalizacionY: {
            valorOriginal: yStr,
            valorNormalizado: _yLimpio,
            exito: true,
            patronDetectado: 'EUROPEO_COMPLETO' as const,
            fasesAplicadas: ['NRM-A01-C2', 'NRM-A01-C3'],
            correccionAplicada: _yMilesEu,
            tipoCorreccion: _yMilesEu ? ('UTM30_MILES_EUROPEOS' as const) : null,
            warnings: _yMilesEu
              ? [`miles-EU: "${yStr}" → ${_yLimpio}`]
              : [],
            errores: [],
          } as ResultadoNormalizacion,
          validacionX: null, validacionY: null,
          intercambioAplicado: false, concatenacionDetectada: false,
          confianzaGlobal: 'ALTA' as const,
          epsg: epsgAsumido,
          validacionGeografica: null,
          codigoINE,
        };
      }
    }
  }

  const normX = normalizarCoordenada(xStr);
  const normY = normalizarCoordenada(yStr);
  
  let x = normX.valorNormalizado;
  let y = normY.valorNormalizado;
  let validX: ResultadoValidacion | null = null;
  let validY: ResultadoValidacion | null = null;
  let intercambioAplicado = false;
  let concatenacionDetectada = false;
  
  // Detectar coordenadas concatenadas o pegadas
  if (detectarConcatenacion) {
    const esPatronFusionado = (patron: PatronDetectado) => 
      patron === 'CONCATENADO' || patron === 'PEGADAS' || patron === 'PAR_ESPACIOS_MULTIPLES'; // IEP-09
    
    if (esPatronFusionado(normX.patronDetectado) && 
        (normY.patronDetectado === 'PLACEHOLDER' || yStr.trim() === '')) {
      const yExtraida = normX._yExtraida;
      if (yExtraida !== undefined) {
        y = yExtraida;
        concatenacionDetectada = true;
        normY.valorNormalizado = yExtraida;
        normY.exito = true;
        normY.patronDetectado = 'CONCATENADO';
        normY.warnings.push('Y extraída de coordenadas concatenadas en campo X');
      }
    }
    
    if (esPatronFusionado(normY.patronDetectado) && 
        (normX.patronDetectado === 'PLACEHOLDER' || xStr.trim() === '')) {
      let esConcatenado: boolean;
      let valorX: number | null;
      let valorY: number | null;
      
      if (normY.patronDetectado === 'PEGADAS') {
        const pegadas = separarCoordenadasPegadas(yStr);
        esConcatenado = pegadas.esPegadas;
        valorX = pegadas.valorX;
        valorY = pegadas.valorY;
      } else {
        const concatenacion = separarCoordenadasConcatenadas(yStr);
        esConcatenado = concatenacion.esConcatenado;
        valorX = concatenacion.valorX;
        valorY = concatenacion.valorY;
      }
      
      if (esConcatenado) {
        x = valorX;
        y = valorY;
        concatenacionDetectada = true;
        normX.valorNormalizado = valorX;
        normX.exito = true;
        normX.patronDetectado = normY.patronDetectado;
        const tipoStr = normY.patronDetectado === 'PEGADAS' ? 'pegadas' : 'concatenadas';
        normX.warnings.push(`X extraída de coordenadas ${tipoStr} en campo Y`);
      }
    }
    
    if (xStr.trim() === yStr.trim() && esPatronFusionado(normX.patronDetectado)) {
      let esConcatenado: boolean;
      let valorX: number | null;
      let valorY: number | null;
      
      if (normX.patronDetectado === 'PEGADAS') {
        const pegadas = separarCoordenadasPegadas(xStr);
        esConcatenado = pegadas.esPegadas;
        valorX = pegadas.valorX;
        valorY = pegadas.valorY;
      } else {
        const concatenacion = separarCoordenadasConcatenadas(xStr);
        esConcatenado = concatenacion.esConcatenado;
        valorX = concatenacion.valorX;
        valorY = concatenacion.valorY;
      }
      
      if (esConcatenado) {
        x = valorX;
        y = valorY;
        concatenacionDetectada = true;
        normX.valorNormalizado = valorX;
        normY.valorNormalizado = valorY;
        const tipoStr = normX.patronDetectado === 'PEGADAS' ? 'pegado' : 'concatenado';
        normX.warnings.push(`Campos X e Y idénticos con valor ${tipoStr} - separados`);
        normY.warnings.push(`Campos X e Y idénticos con valor ${tipoStr} - separados`);
      }
    }
  }
  
  // Detectar formatos espaciales (WKT, GeoJSON)
  const esFormatoEspacialX = normX.patronDetectado === 'WKT_POINT' || 
                             normX.patronDetectado === 'GEOJSON_POINT';
  const esFormatoEspacialY = normY.patronDetectado === 'WKT_POINT' || 
                             normY.patronDetectado === 'GEOJSON_POINT';
  
  if (esFormatoEspacialX && (normY.patronDetectado === 'PLACEHOLDER' || yStr.trim() === '')) {
    const yExtraida = normX._yExtraida;
    if (yExtraida !== undefined) {
      y = yExtraida;
      normY.valorNormalizado = yExtraida;
      normY.exito = true;
      normY.patronDetectado = normX.patronDetectado;
      normY.warnings.push(`Y extraída de formato ${normX.patronDetectado} en campo X`);
    }
  }
  
  if (esFormatoEspacialY && (normX.patronDetectado === 'PLACEHOLDER' || xStr.trim() === '')) {
    const espacial = detectarFormatoEspacial(yStr);
    if (espacial.esEspacial) {
      x = espacial.valorX;
      y = espacial.valorY;
      normX.valorNormalizado = espacial.valorX;
      normX.exito = true;
      normX.patronDetectado = espacial.formato!;
      normX.warnings.push(`X extraída de formato ${espacial.formato} en campo Y`);
    }
  }
  
  if (xStr.trim() === yStr.trim() && esFormatoEspacialX) {
    const espacial = detectarFormatoEspacial(xStr);
    if (espacial.esEspacial) {
      x = espacial.valorX;
      y = espacial.valorY;
      normX.valorNormalizado = espacial.valorX;
      normY.valorNormalizado = espacial.valorY;
      normX.warnings.push(`Campos idénticos con formato ${espacial.formato} - separados`);
    }
  }
  
  // Validar
  if (x !== null) {
    validX = validarCoordenada(x);
    if (aplicarCorreccionP0 && validX.valorCorregido !== undefined) {
      x = validX.valorCorregido;
    }
  }
  
  if (y !== null) {
    validY = validarCoordenada(y);
    if (aplicarCorreccionP0 && validY.valorCorregido !== undefined) {
      y = validY.valorCorregido;
    }
  }
  
  // Detectar intercambio X↔Y
  if (detectarIntercambio && x !== null && y !== null) {
    const resultadoIntercambio = detectarIntercambioXY(x, y);
    if (resultadoIntercambio.intercambiado) {
      x = resultadoIntercambio.xCorregida;
      y = resultadoIntercambio.yCorregida;
      intercambioAplicado = true;
      validX = validarCoordenada(x);
      validY = validarCoordenada(y);
      if (validX) validX.warnings.push(resultadoIntercambio.mensaje!);
    }
  }
  
  // VAL-02: CORRECCIÓN AUTOMÁTICA DE Y TRUNCADA
  if (codigoINE && x !== null && y !== null && isPotentiallyTruncated(y)) {
    const yOriginal = y;
    const candidatas: Array<{ y: number; tipo: 'INSERT_4' | 'MULTIPLY_10' }> = [];
    
    // Estrategia 1: TruncatedCoordinateDetector (PREFIX_4 → INSERT_4)
    const truncationResult: TruncationResult = detectTruncation(y, codigoINE);
    if (truncationResult.isTruncated && truncationResult.correctedValue) {
      candidatas.push({ y: truncationResult.correctedValue, tipo: 'INSERT_4' });
    }
    
    // Estrategia 2: MULTIPLY_10 (413364 → 4133640)
    const yMultiplied = y * 10;
    if (yMultiplied >= Y_MIN_ANDALUCIA && yMultiplied <= Y_MAX_ANDALUCIA) {
      candidatas.push({ y: yMultiplied, tipo: 'MULTIPLY_10' });
    }
    
    let mejorCorreccion: { y: number; tipo: 'INSERT_4' | 'MULTIPLY_10' } | null = null;
    let menorDistancia = Infinity;
    
    for (const cand of candidatas) {
      const validacion = validarDistanciaCentroide(x, cand.y, codigoINE);
      if (validacion.exito && validacion.distanciaKm < menorDistancia) {
        menorDistancia = validacion.distanciaKm;
        mejorCorreccion = cand;
      }
    }
    
    // Aplicar si mejora significativamente (< 30km del centroide)
    if (mejorCorreccion && menorDistancia < UMBRAL_CORRECCION_DISTANCIA_KM) {
      y = mejorCorreccion.y;
      normY.correccionAplicada = true;
      normY.tipoCorreccion = mejorCorreccion.tipo;
      normY.valorNormalizado = mejorCorreccion.y;
      normY.warnings.push(`Y truncada corregida: ${yOriginal} → ${y} (${mejorCorreccion.tipo})`);
    }
  }
  
  // VAL-03: CORRECCIÓN AUTOMÁTICA DE COORDENADA CON DÍGITO EXTRA (F2.06)
  // Cuando Y tiene 8 dígitos (esperamos 7) o X tiene 7 dígitos (esperamos 6),
  // genera variantes eliminando 1 dígito y selecciona la más coherente con el centroide.
  if (codigoINE && x !== null && y !== null) {
    const { UTM } = RANGOS_ANDALUCIA;
    const yIntStr = String(Math.floor(Math.abs(y)));
    const xIntStr = String(Math.floor(Math.abs(x)));
    
    // Y con dígito extra: 8 dígitos cuando esperamos 7
    if (yIntStr.length === 8 && y > UTM.Y_MAX) {
      // F2.06a: extrae parte decimal para preservarla en los candidatos
      const yDecStr = y % 1 !== 0 ? (String(y).split('.')[1] ?? '') : '';
      const candidates = generateDigitRemovalCandidates(yIntStr, 7, yDecStr);
      if (candidates.length > 0) {
        // Adapter CoordinateCandidate → CoordinateVariant (source → original)
        const variants = candidates.map(c => ({
          value: c.value,
          removedDigitIndex: c.removedDigitIndex,
          removedDigit: c.removedDigit,
          original: c.source,
        }));
        const ranked = rankCoordinateVariants(variants, x, 'y', codigoINE);
        if (ranked.length > 0 && ranked[0].score >= UMBRAL_SCORE_VARIANTE) {
          const best = ranked[0];
          const yOriginalExtra = y;
          y = best.value;
          normY.correccionAplicada = true;
          normY.tipoCorreccion = 'EXTRA_DIGIT_REMOVAL';
          normY.valorNormalizado = best.value;
          normY.warnings.push(
            `Y dígito extra corregida: ${yOriginalExtra} → ${y} (eliminado '${best.removedDigit}' pos ${best.removedDigitIndex}, score ${best.score})`
          );
        }
      }
    }
    
    // X con dígito extra: 7 dígitos cuando esperamos 6
    if (xIntStr.length === 7 && x > UTM.X_MAX) {
      // F2.06a: extrae parte decimal para preservarla en los candidatos
      const xDecStr = x % 1 !== 0 ? (String(x).split('.')[1] ?? '') : '';
      const candidates = generateDigitRemovalCandidates(xIntStr, 6, xDecStr);
      if (candidates.length > 0) {
        // Adapter CoordinateCandidate → CoordinateVariant (source → original)
        const variants = candidates.map(c => ({
          value: c.value,
          removedDigitIndex: c.removedDigitIndex,
          removedDigit: c.removedDigit,
          original: c.source,
        }));
        const ranked = rankCoordinateVariants(variants, y, 'x', codigoINE);
        if (ranked.length > 0 && ranked[0].score >= UMBRAL_SCORE_VARIANTE) {
          const best = ranked[0];
          const xOriginalExtra = x;
          x = best.value;
          normX.correccionAplicada = true;
          normX.tipoCorreccion = 'EXTRA_DIGIT_REMOVAL';
          normX.valorNormalizado = best.value;
          normX.warnings.push(
            `X dígito extra corregida: ${xOriginalExtra} → ${x} (eliminado '${best.removedDigit}' pos ${best.removedDigitIndex}, score ${best.score})`
          );
        }
      }
    }
  }
  
  // VAL-01: VALIDACIÓN GEOGRÁFICA POR DISTANCIA AL CENTROIDE
  let validacionGeografica: ResultadoDistanciaCentroide | null = null;
  
  if (codigoINE && x !== null && y !== null) {
    validacionGeografica = validarDistanciaCentroide(x, y, codigoINE);
    
    if (validacionGeografica.exito) {
      const distancia = validacionGeografica.distanciaKm;
      
      if (distancia > UMBRAL_DISTANCIA_WARNING_KM) {
        const warningMsg = `⚠️ Coordenada a ${distancia.toFixed(1)}km del centroide municipal (umbral: ${UMBRAL_DISTANCIA_WARNING_KM}km)`;
        normX.warnings.push(warningMsg);
      }
      
      if (distancia > UMBRAL_DISTANCIA_BAJA_KM) {
        const warningCritico = `❌ Distancia excesiva: ${distancia.toFixed(1)}km - Verificar coordenadas o municipio`;
        normX.warnings.push(warningCritico);
      }
    }
  }
  
  // M05 — RESCATE SWAP X↔Y (última defensa)
  // Se activa cuando el par sigue fuera de rango o lejos del centroide tras todas las correcciones.
  if (detectarIntercambio && x !== null && y !== null && !intercambioAplicado) {
    const { UTM } = RANGOS_ANDALUCIA;
    const xFueraRango = x < UTM.X_MIN || x > UTM.X_MAX;
    const yFueraRango = y < UTM.Y_MIN || y > UTM.Y_MAX;
    const distanciaExcesiva = validacionGeografica?.exito && validacionGeografica.distanciaKm > UMBRAL_DISTANCIA_BAJA_KM;

    if (xFueraRango || yFueraRango || distanciaExcesiva) {
      const rescate = intentarRescateSwap(x, y);
      if (rescate.intercambiado || rescate.xCorregida !== x || rescate.yCorregida !== y) {
        x = rescate.xCorregida;
        y = rescate.yCorregida;
        intercambioAplicado = true;
        validX = validarCoordenada(x);
        validY = validarCoordenada(y);
        if (validX && rescate.mensaje) validX.warnings.push(rescate.mensaje);
        // Re-validar distancia al centroide con coordenadas corregidas
        if (codigoINE) {
          validacionGeografica = validarDistanciaCentroide(x, y, codigoINE);
        }
      }
    }
  }

  // ED50INF (DOP-1 a, Luis 5-nov-2026): retirado el «fallback» NRM-B01 que convertía ED50 → ETRS89 una fila
  // suelta cuando caía a 150-280 m de la cabecera municipal y al convertirla se acercaba a ella. Ese indicio
  // solo es geométrico: en el corpus marcó 18 filas y en las 18 acertaba la coordenada escrita (Catastro bajo
  // el punto con la dirección del documento, códigos plus a 1 m; `.ptel/p0_ed50inf_20261105/AUDIT_ED50INF.md`).
  // El ED50 se convierte solo si se declara (`datumHint: 'ED50'`); detectarlo por documento es
  // `OBS-COORD-LA-DETECCION-ED50-DEL-DOCUMENTO-NO-LLEGA-A-LA-NORMALIZACION`.

  // Determinar confianza global
  let confianzaGlobal: NivelConfianza = 'ALTA';
  if (x === null || y === null) {
    confianzaGlobal = 'CRITICA';
  } else if (concatenacionDetectada) {
    confianzaGlobal = 'MEDIA';
  } else if (intercambioAplicado || validX?.confianza === 'MEDIA' || validY?.confianza === 'MEDIA') {
    confianzaGlobal = 'MEDIA';
  } else if (validX?.confianza === 'BAJA' || validY?.confianza === 'BAJA') {
    confianzaGlobal = 'BAJA';
  }
  
  // VAL-01: Degradar confianza según distancia al centroide
  if (validacionGeografica?.exito) {
    const distancia = validacionGeografica.distanciaKm;
    
    if (distancia > UMBRAL_DISTANCIA_BAJA_KM && confianzaGlobal !== 'CRITICA') {
      confianzaGlobal = 'BAJA';
    } else if (distancia > UMBRAL_DISTANCIA_WARNING_KM && confianzaGlobal === 'ALTA') {
      confianzaGlobal = 'MEDIA';
    }
  }
  
  return {
    x,
    y,
    xOriginal: xStr,
    yOriginal: yStr,
    normalizacionX: normX,
    normalizacionY: normY,
    validacionX: validX,
    validacionY: validY,
    intercambioAplicado,
    concatenacionDetectada,
    confianzaGlobal,
    epsg: epsgAsumido,
    validacionGeografica,
    codigoINE,
  };
}
